/*
 * Copyright (c) Dito Technologies
 */

import React, {useContext} from 'react';
import {Helmet} from "react-helmet";
import {BrowserRouter, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import BackdoorLogin from "ui/feature/backdoor-login/BackdoorLogin";
import Root from "ui/feature/root/Root";
import Authentication from "state/authentication";
import AuthenticationContext from "state/authentication";
import {getAnalytics, logEvent} from "firebase/analytics";
import UseAuthentication from "hooks/UseAuthentication";
import Account from "ui/feature/account/Account";
import Library from "ui/feature/library/Library";
import * as paths from "constants/paths";
import {PathRequiresAuthentication} from "constants/paths";
import LibraryDetail from "ui/feature/library-detail/LibraryDetail";
import PieceEdit from "ui/feature/piece-edit/PieceEdit";
import PieceOriginalImages from "ui/feature/piece-original-images/PieceOriginalImages";
import PieceSettings from "ui/feature/piece-settings/PieceSettings";
import PiecePlay from "ui/feature/piece-play/PiecePlay";
import NavigationBar from "ui/feature/common/NavigationBar";
import Origin from "ui/feature/origin/Origin";
import Login from "ui/feature/login/Login";
import UseMIDI from "hooks/UseMIDI";
import MIDI from "state/midi";

const Paths: React.FC = () => {

    const authenticationContext = useContext(AuthenticationContext);
    const [redirectOnceAuthenticated, setRedirectOnceAuthenticated] = React.useState<string>(paths.ROOT_PATH);
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        const path = location.pathname;
        const requiresAuthentication = PathRequiresAuthentication(path);
        if (!authenticationContext.initialLoadDone) {
            return;
        }
        if (requiresAuthentication && !authenticationContext.isAuthenticated) {
            setRedirectOnceAuthenticated(path);
            navigate(paths.ORIGIN_PATH)
        } else if (!requiresAuthentication && authenticationContext.isAuthenticated) {
            navigate(redirectOnceAuthenticated)
        }
    }, [location, authenticationContext, navigate, redirectOnceAuthenticated]);

    React.useEffect(() => {
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: location.pathname,
            firebase_screen_class: location.pathname
        });
    }, [location.pathname]);

    if (!authenticationContext.initialLoadDone) {
        return null; // wait for lead from local storage
    }

    return (
        <>
            <NavigationBar/>
            <Routes>
                <Route path={paths.PIECE_PLAY_TEMPLATE} element={<PiecePlay/>}/>
                <Route path={paths.PIECE_EDIT_TEMPLATE} element={<PieceEdit/>}/>
                <Route path={paths.LIBRARY_DETAIL_TEMPLATE} element={<LibraryDetail/>}/>
                <Route path={paths.PIECE_ORIGINAL_IMAGES_TEMPLATE} element={<PieceOriginalImages/>}/>
                <Route path={paths.PIECE_SETTINGS_TEMPLATE} element={<PieceSettings/>}/>
                <Route path={paths.LIBRARY_PATH} element={<Library/>}/>
                <Route path={paths.ACCOUNT_PATH} element={<Account/>}/>
                <Route path={paths.BACKDOOR_LOGIN} element={<BackdoorLogin/>}/>
                <Route path={paths.LOGIN_PATH} element={<Login/>}/>
                <Route path={paths.ROOT_PATH} element={<Root/>}/>
                <Route path={paths.ORIGIN_PATH} element={<Origin/>}/>
            </Routes>
        </>
    );
}

const App: React.FC = () => {

    const authenticationValue = UseAuthentication();
    const midiValue = UseMIDI();

    return (
        <>
            <Helmet>
                <title>Music</title>
            </Helmet>
            <MIDI.Provider value={midiValue}>
                <Authentication.Provider value={authenticationValue}>
                    <BrowserRouter>
                        <Paths/>
                    </BrowserRouter>
                </Authentication.Provider>
            </MIDI.Provider>
        </>
    );
}

export default App;
